/**
 * Utility functions for handling URLs in the application
 */

/**
 * Gets the base URL for the application.
 * In browser, uses window.location, in server uses environment variable or default.
 */
export function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    // Client-side: use window.location
    const { protocol, host } = window.location;
    return `${protocol}//${host}`;
  }

  // Server-side: use environment variable or default
  return process.env.NEXT_PUBLIC_SITE_URL || process.env.VERCEL_URL
    ? `https://${process.env.VERCEL_URL}`
    : "https://avezor.com";
}

/**
 * Formats a path to be properly formatted with leading slash
 */
export function formatPath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Ensures a pathname has a trailing slash
 * Required for Next.js apps with trailingSlash: true configuration
 */
export function ensureTrailingSlash(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

/**
 * Joins base URL with a path, handling slashes properly
 */
export function joinUrl(baseUrl: string, path: string): string {
  if (!path) {
    return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  }

  const formattedPath = formatPath(path);
  const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

  return `${base}${formattedPath}`;
}

/**
 * Extract query parameters from a URL
 */
export function extractQueryParams(url: string): Record<string, string> {
  try {
    const urlObj = new URL(url);
    const params: Record<string, string> = {};

    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  } catch (e) {
    console.error(e);
    // Handle relative URLs or invalid URLs
    const queryStart = url.indexOf("?");
    if (queryStart === -1) {
      return {};
    }

    const queryString = url.slice(queryStart + 1);
    const pairs = queryString.split("&");

    return pairs.reduce((params: Record<string, string>, pair) => {
      const [key, value] = pair.split("=");
      if (key) {
        params[key] = value || "";
      }
      return params;
    }, {});
  }
}
